import { SHOW_AUTO_DIGITIZE_HOME } from "../../../config/autoDigitize";
import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { uploadedSVGsInStorage, fetchPublicFilesWithUrls } from "../../../lib/firebase/firebase";
import {
  setUploadedFiles,
  appendUploadedFiles,
  setLastDoc,
  setHasMore,
} from "../../../redux/slices/UploadedFiles";
import { setCurrentTab } from "../../../redux/slices/CurrentTab";
import { useAuth } from "../../../contexts/AuthContext";
import { isNativeiOS } from "../../../lib/utils/platform";
import IOSPaywall from "../../PaywallModal/IOSPaywall";
import { isEmbroideryTrace } from "../../../config/appVariant";
import "./styles.css";
import Card from "./Card";
import noDataImage from "../../../assets/no_data_image_2.svg";
import { Upload } from "antd";
import { IonIcon } from "@ionic/react";
import { cloudUploadOutline, brush, peopleOutline, expandOutline, shapesOutline, schoolOutline } from "ionicons/icons";
import { useContext } from "react";
import { AppContext } from "../../../contexts/AppContext";
import { queueUploadImage } from "../../../lib/upload/queueUploadImage";
import { useTutorial } from "../../Tutorial";
import { TUTORIAL_DESIGNS, TUTORIAL_TOTAL_MINUTES, PICK_DESIGN_STEP } from "../../../tutorials";
import TutorialMascot from "../../Tutorial/game/TutorialMascot";
import { ReactComponent as EmbroideryIcon } from "../../../assets/embroidery_icon.svg";

const All = ({ setRerender, rerender, setIsImageGeneratorModalVisible, onSvgFile, onAutoDigitizeFile, autoDigitizeBusy }) => {
  const dispatch = useDispatch();
  const { user, userIsPremium } = useAuth();
  const [showIOSPaywall, setShowIOSPaywall] = useState(false);
  const { setImageSource, setImage64, debugEmptyState, setPendingEmbroideryMode } = useContext(AppContext);
  const { triggerAction, startTutorial, completedLessons } = useTutorial();

  // The interactive examples are the embroidery lessons, so the card only makes
  // sense in that variant — SvgTrace's walkthrough has no designs to pick from.
  const completedExamples = TUTORIAL_DESIGNS.filter(
    (design) => completedLessons?.has(design.tutorialId)
  ).length;
  // Half-finished sets are the easiest thing to abandon, so the card names the
  // lesson that's next and what it costs rather than reporting a score.
  const nextLesson = TUTORIAL_DESIGNS.find(
    (design) => !completedLessons?.has(design.tutorialId)
  );
  const lessonsLeft = TUTORIAL_DESIGNS.length - completedExamples;
  // `startTutorial` refuses to run on phones and the iOS app, so anywhere it
  // can't run the lessons aren't offered.
  const academyAvailable = isEmbroideryTrace && !isNativeiOS() && window.innerWidth > 768;
  const { uploadedFiles, lastDoc, hasMore } = useSelector(
    (state) => state.uploadedFiles
  );
  const currentTab = useSelector((state) => state.currentTab.tab);
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const observerRef = useRef();
  const requestInFlight = useRef(false);
  const containerRef = useRef();
  const itemsPerPage = 24;
  const [communityPreviewImages, setCommunityPreviewImages] = useState([]);
  const [showSkeleton, setShowSkeleton] = useState(uploadedFiles.length === 0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  useEffect(() => {
    if (user && uploadedFiles.length === 0 && isInitialLoad) {
      getUploadedFiles();
    } else if (uploadedFiles.length > 0 && isInitialLoad) {
      setIsInitialLoad(false);
      setShowSkeleton(false);
    }
  }, [user, uploadedFiles.length, isInitialLoad]);

  const prevTabRef = useRef(currentTab);
  // Refresh when user switches TO Recent Files tab (e.g. after upload/convert flow)
  useEffect(() => {
    const switchedToRecent = prevTabRef.current !== "carousel" && currentTab === "carousel";
    prevTabRef.current = currentTab;
    if (switchedToRecent && user && !loading) {
      getUploadedFiles();
    }
  }, [currentTab]);

  // Fetch community preview images for empty state
  useEffect(() => {
    if (isEmbroideryTrace || isInitialLoad || uploadedFiles.length > 0) return;
    const fetchCommunityPreview = async () => {
      try {
        const result = await fetchPublicFilesWithUrls(0, 8); // Only the eight thumbnails shown in the empty state.
        console.log('🖼️ Fetched community preview images:', result.files?.length || 0);
        setCommunityPreviewImages(result.files || []);
      } catch (error) {
        console.error('Error fetching community preview:', error);
      }
    };
    
    fetchCommunityPreview();
  }, [isInitialLoad, uploadedFiles.length]);

  const fetchMoreDataCallback = useCallback(() => {
    fetchMoreData();
  }, [loading, isLoadingMore, hasMore, user, lastDoc, itemsPerPage]);

  const getUploadedFiles = async () => {
    if (!user || requestInFlight.current) {
      return;
    }

    requestInFlight.current = true;
    try {
      setLoading(true);
      setError(null);
      console.log("All.jsx: Starting to fetch uploaded files for user:", user.uid);

      const {
        files,
        lastDoc: newLastDoc,
        hasMore: newHasMore,
      } = await uploadedSVGsInStorage(null, itemsPerPage);
      
      console.log("All.jsx: Fetched files:", files.length, "files");
      console.log("All.jsx: Files data:", files);
      
      dispatch(setUploadedFiles(files));
      dispatch(setLastDoc(newLastDoc));
      dispatch(setHasMore(newHasMore));
      setIsInitialLoad(false);
      setShowSkeleton(false);
    } catch (error) {
      console.error("All.jsx: Error getting files:", error);
      console.error("All.jsx: Error details:", {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      // Set user-friendly error message
      if (error.code === 'permission-denied') {
        setError("Permission denied. Please check your account permissions.");
      } else if (error.message?.includes('insufficient permissions')) {
        setError("Insufficient permissions to access files. Please contact support.");
      } else {
        setError("Failed to load files. Please try again later.");
      }
      setIsInitialLoad(false);
      setShowSkeleton(false);
    } finally {
      requestInFlight.current = false;
      setLoading(false);
    }
  };

  const fetchMoreData = async () => {
    if (requestInFlight.current || loading || isLoadingMore || !hasMore || !user) {
      console.log("All.jsx: Skipping fetchMoreData -", { loading, isLoadingMore, hasMore, hasUser: !!user });
      return;
    }

    requestInFlight.current = true;
    try {
      setIsLoadingMore(true);
      console.log("All.jsx: Fetching more data with lastDoc:", lastDoc?.id);
      
      const result = await uploadedSVGsInStorage(lastDoc, itemsPerPage);

      // The cursor follows raw documents, even when missing/non-SVG files
      // are filtered out. A short visible page does not mean we reached the end.
      dispatch(setLastDoc(result.lastDoc));
      dispatch(setHasMore(result.hasMore));
      dispatch(appendUploadedFiles(result.files));
    } catch (error) {
      console.error("All.jsx: Error fetching more files:", error);
    } finally {
      requestInFlight.current = false;
      setIsLoadingMore(false);
    }
  };

  const recentDesigns = useMemo(() => {
    const uploadedFilesArray = Array.isArray(uploadedFiles)
      ? uploadedFiles
      : [];
    return uploadedFilesArray
      .map((file) => {
        const dateObj = file.updatedAt
          ? new Date(file.updatedAt)
          : file.createdAt && file.createdAt.seconds
          ? new Date(file.createdAt.seconds * 1000)
          : null;
        const date = dateObj && !isNaN(dateObj.getTime())
          ? dateObj.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : undefined;
        return {
          ...file,
          timestamp: dateObj,
          date,
        };
      })
      .sort((a, b) => b.timestamp - a.timestamp);
  }, [uploadedFiles]);

  const lastElementRef = useCallback(
    (node) => {
      if (loading || isLoadingMore) return;

      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          fetchMoreDataCallback();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [loading, isLoadingMore, hasMore, fetchMoreDataCallback]
  );

  const isSparse = recentDesigns.length <= 3;
  const phantomCount = useMemo(() => {
    if (!isSparse) return 0;
    const gridContainer = containerRef.current?.querySelector('.sparse-recent-row');
    let columns = 5;
    if (gridContainer) {
      const style = window.getComputedStyle(gridContainer);
      const cols = style.gridTemplateColumns;
      if (cols && cols !== 'none') {
        columns = cols.split(' ').length;
      }
    } else {
      const isMobile = viewportWidth <= 768;
      if (isMobile) {
        columns = 3;
      } else {
        const sidebarWidth = viewportWidth > 600 ? 120 : 32;
        const containerWidth = viewportWidth - sidebarWidth - 64;
        const isTablet = viewportWidth >= 768 && viewportWidth < 1200;
        const itemMinWidth = isTablet ? 174 : 241;
        columns = Math.max(2, Math.floor(containerWidth / itemMinWidth));
      }
    }
    const isMobileView = viewportWidth <= 768;
    const availableHeight = window.innerHeight - (isMobileView ? 280 : 360);
    const rowHeight = isMobileView ? 130 : 260;
    const rowsThatFit = Math.max(3, Math.ceil(availableHeight / rowHeight));
    const totalSlots = columns * rowsThatFit;
    const remainder = (recentDesigns.length % columns);
    const minToFillRows = remainder === 0 ? 0 : columns - remainder;
    return Math.max(minToFillRows, totalSlots - recentDesigns.length);
  }, [isSparse, viewportWidth, recentDesigns.length]);

  if (!user) {
    return (
      <div className="no-data-container">
        <img src={noDataImage} alt="no data" style={{ maxWidth: "150px" }} />
        <p className="no-text">
          Please sign in to view your files.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="no-data-container">
        <img src={noDataImage} alt="error" style={{ maxWidth: "150px" }} />
        <p className="no-text" style={{ color: '#ff4757' }}>
          {error}
        </p>
        <button 
          onClick={() => {
            setError(null);
            getUploadedFiles();
          }}
          style={{
            marginTop: '16px',
            padding: '8px 16px',
            background: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  const loadingRecent = recentDesigns.length === 0 && (loading || showSkeleton);

  if (!loadingRecent && ((recentDesigns.length === 0 && !loading) || debugEmptyState)) {
    const isMobileEmpty = viewportWidth <= 768;

    if (isMobileEmpty) {
      return (
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "48px 24px",
          textAlign: "center",
          minHeight: "40vh",
        }}>
          <div style={{
            width: "72px",
            height: "72px",
            borderRadius: "20px",
            background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "20px",
          }}>
            <IonIcon icon={cloudUploadOutline} style={{ fontSize: "32px", color: "#94a3b8" }} />
          </div>
          <h3 style={{
            fontFamily: "Clash Grotesk, -apple-system, sans-serif",
            fontSize: "20px",
            fontWeight: 600,
            color: "#1e293b",
            margin: "0 0 8px",
          }}>No files yet</h3>
          <p style={{
            fontFamily: "Clash Grotesk, -apple-system, sans-serif",
            fontSize: "14px",
            color: "#94a3b8",
            margin: 0,
            lineHeight: 1.5,
            maxWidth: "260px",
          }}>
            {isEmbroideryTrace ? "Upload an image using the button above" : "Upload an image or generate one with AI using the buttons above"}
          </p>
        </div>
      );
    }

    if (isEmbroideryTrace) {
      return (
        <div className="emb-empty-wrapper">
        <div className="emb-empty-state">
          {/* Header */}
          <div className="emb-empty-header">
            <h2 className="emb-empty-title">Digitize your first design</h2>
            <p className="emb-empty-subtitle">
              {academyAvailable
                ? "Learn the whole workflow in a short lesson, or start straight from your own image — either way you'll finish with a file your machine can sew."
                : "Upload your image to start creating machine-embroidery-ready stitches."}
            </p>
          </div>

          {/* The lessons are the fastest route to a file someone can actually
              stitch, and this screen is the only one a new account sees — so the
              Academy leads, and the blank-page options follow it. */}
          {academyAvailable && (
            <button
              type="button"
              className="emb-academy-card"
              onClick={() => startTutorial('embroidery', PICK_DESIGN_STEP)}
            >
              <span className="emb-academy-badge">
                {!nextLesson ? 'Complete' : completedExamples > 0 ? 'In progress' : 'Start here'}
              </span>
              <span className="emb-academy-mascot">
                <TutorialMascot talking={false} cheering={false} size={62} />
              </span>
              <span className="emb-academy-text">
                <span className="emb-academy-title">Learn it in the Academy</span>
                <span className="emb-academy-desc">
                  {!nextLesson
                    ? `All ${TUTORIAL_DESIGNS.length} lessons finished — replay any of them.`
                    : completedExamples > 0
                      ? `${lessonsLeft} to go — next up is ${nextLesson.name}, about ${nextLesson.minutes} minutes.`
                      : `${TUTORIAL_DESIGNS.length} guided lessons, about ${TUTORIAL_TOTAL_MINUTES} minutes in all. The first one walks you to a finished stitch file in ${nextLesson.minutes}.`}
                </span>
              </span>
              <span className="emb-academy-cta">
                {!nextLesson ? 'Replay' : completedExamples > 0 ? 'Resume' : 'Start'}
              </span>
            </button>
          )}

          {academyAvailable && (
            <p className="emb-empty-divider">Or start from your own image</p>
          )}

          {/* Action rows */}
          <div className="emb-action-rows">
            {SHOW_AUTO_DIGITIZE_HOME && <Upload
              beforeUpload={(file) => {
                if (onAutoDigitizeFile) onAutoDigitizeFile(file);
                return false;
              }}
              showUploadList={false}
              accept="image/png,image/jpeg,image/webp"
              disabled={autoDigitizeBusy}
              className="emb-upload-row-trigger"
            >
              <div className="emb-action-row emb-action-row--auto">
                <div className="emb-action-row-icon emb-action-row-icon--auto">✦</div>
                <div className="emb-action-row-text">
                  <span className="emb-action-row-title">Auto-digitize with Trace <small>BETA</small></span>
                  <span className="emb-action-row-desc">Image → editable stitch project</span>
                </div>
                <div className="emb-action-row-btn emb-action-row-btn--auto">{autoDigitizeBusy ? "Working…" : "Choose image"}</div>
              </div>
            </Upload>}
            <Upload
              beforeUpload={(file) => {
                if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) return Upload.LIST_IGNORE;
                const reader = new FileReader();
                reader.onload = (e) => {
                  queueUploadImage(dispatch, e.target.result);
                  setImageSource("uploaded");
                  setImage64(e.target.result);
                  triggerAction('file-uploaded', { fileName: file.name, fileType: file.type });
                };
                reader.readAsDataURL(file);
                return false;
              }}
              showUploadList={false}
              accept="image/*"
              className="emb-upload-row-trigger"
            >
              <div className="emb-action-row emb-action-row--primary">
                <div className="emb-action-row-icon">
                  <IonIcon icon={cloudUploadOutline} />
                </div>
                <div className="emb-action-row-text">
                  <span className="emb-action-row-title">Upload an image</span>
                  <span className="emb-action-row-desc">JPG, PNG or SVG → auto-converted to stitches</span>
                </div>
                <div className="emb-action-row-btn emb-action-row-btn--pink">Upload</div>
              </div>
            </Upload>

          </div>

          {/* Workflow steps */}
          <div className="emb-steps-section">
            <p className="emb-steps-label">How it works</p>
            <div className="emb-steps">
              {[
                { n: "1", title: "Upload your image", desc: "Start with your own artwork" },
                { n: "2", title: "Edit in Stitch Editor", desc: "Adjust colors, stitch types & density" },
                { n: "3", title: "Export your file", desc: "Download DST, PES, JEF and more" },
              ].map((step) => (
                <div key={step.n} className="emb-step">
                  <div className="emb-step-num">{step.n}</div>
                  <div className="emb-step-text">
                    <span className="emb-step-title">{step.title}</span>
                    <span className="emb-step-desc">{step.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        </div>
      );
    }

    return (
      <>
      <div className="no-files-welcome-container">
        <div className="welcome-content">
          <div className="welcome-header">
            <h2 className="welcome-title">No files yet</h2>
            <p className="welcome-subtitle">{isEmbroideryTrace ? "Start creating your first embroidery design by uploading an image or generating one with AI" : "Start creating your first SVG by uploading an image or generating one with AI"}</p>
          </div>
          
          <div className="welcome-actions">
            <div className="action-grid">
              <Upload
                beforeUpload={(file) => {
                  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
                    console.log("Unsupported file format");
                    return Upload.LIST_IGNORE;
                  }
                  const reader = new FileReader();
                  reader.onload = (e) => {
                    queueUploadImage(dispatch, e.target.result);
                    setImageSource("uploaded");
                    setImage64(e.target.result);
                    
                    triggerAction('file-uploaded', { 
                      fileName: file.name, 
                      fileType: file.type 
                    });
                  };
                  reader.readAsDataURL(file);
                  return false;
                }}
                showUploadList={false}
                accept="image/*"
                className="welcome-upload-action"
                data-tutorial="upload-button"
              >
                <div className="action-card primary-action">
                  <div className="action-icon">
                    <IonIcon icon={cloudUploadOutline} />
                  </div>
                  <h3 className="action-title">Upload Image</h3>
                  <p className="action-description">{isEmbroideryTrace ? "Upload your JPG, PNG, or WebP file to convert to stitches" : "Upload your JPG, PNG, or WebP file to convert to SVG"}</p>
                </div>
              </Upload>
              
              <div 
                className="action-card secondary-action"
                onClick={() => {
                  if (isNativeiOS() && !userIsPremium) {
                    setShowIOSPaywall(true);
                    return;
                  }
                  if (setIsImageGeneratorModalVisible) {
                    setIsImageGeneratorModalVisible(true);
                  }
                }}
                style={{ cursor: 'pointer' }}
                data-action="generate"
                data-onboarding-target="ai-generate"
              >
                <div className="action-icon">
                  <IonIcon icon={brush} />
                </div>
                <h3 className="action-title">Generate with AI</h3>
                <p className="action-description">{isEmbroideryTrace ? "Create an image using AI and convert it to stitches" : "Create an image using AI and convert it to SVG"}</p>
                <div className="action-button">Generate</div>
              </div>

              {!isEmbroideryTrace && (
              <div className="community-preview-card">
                <div className="community-preview-header">
                  <div className="community-header-content">
                    <div className="community-icon">
                      <IonIcon icon={peopleOutline} />
                    </div>
                    <div className="community-header-text">
                      <h3 className="community-title">Explore Community</h3>
                      <p className="community-subtitle">Recent creations from our users</p>
                    </div>
                  </div>
                  <div 
                    className="community-see-all-button"
                    onClick={() => {
                      dispatch(setCurrentTab("community"));
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    See All
                  </div>
                </div>
                
                <div className="community-preview-grid">
                  {communityPreviewImages.slice(0, 8).map((image, index) => (
                    <div key={image.id || index} className="community-preview-item">
                      <img 
                        src={image.downloadURL || image.url} 
                        alt={`Community creation ${index + 1}`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  ))}
                  {Array.from({ length: Math.max(0, 8 - communityPreviewImages.length) }).map((_, index) => (
                    <div key={`placeholder-${index}`} className="community-preview-item placeholder">
                      <IonIcon icon={peopleOutline} />
                    </div>
                  ))}
                </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showIOSPaywall && (
        <IOSPaywall
          standalone
          action="generate"
          onClose={() => setShowIOSPaywall(false)}
          onSuccess={() => setShowIOSPaywall(false)}
        />
      )}
      </>
    );
  }

  return (
    <>
      <div className="all-container" ref={containerRef}>
        <div className="sparse-home">
          <div className="sparse-section-header">
            <h2 className="sparse-title">Start something new</h2>
          </div>

          <div className="sparse-new-row">
            {isEmbroideryTrace && SHOW_AUTO_DIGITIZE_HOME && (
              <Upload
                accept="image/jpeg,image/png,image/webp"
                beforeUpload={(file) => {
                  if (onAutoDigitizeFile) onAutoDigitizeFile(file);
                  return false;
                }}
                showUploadList={false}
                multiple={false}
                disabled={autoDigitizeBusy}
              >
                <div className="sparse-new-card sparse-new-card--auto">
                  <span className="sparse-new-card-badge">Beta</span>
                  <div className="sparse-new-card-icon sparse-new-card-icon--auto">✦</div>
                  <span className="sparse-new-card-label">Auto-digitize with Trace</span>
                  <span className="sparse-new-card-sub">{autoDigitizeBusy ? "Trace is working…" : "Image → editable stitch project"}</span>
                </div>
              </Upload>
            )}
            <Upload
              accept="image/jpeg,image/png,image/webp,image/svg+xml,.svg"
              beforeUpload={(file) => {
                const isSvg = file.type === "image/svg+xml" || /\.svg$/i.test(file.name || "");
                if (isSvg) {
                  if (onSvgFile) onSvgFile(file);
                  return false;
                }
                if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) return Upload.LIST_IGNORE;
                const reader = new FileReader();
                reader.onload = (e) => {
                  if (setImage64) setImage64(e.target.result);
                  if (setImageSource) setImageSource("uploaded");
                };
                reader.readAsDataURL(file);
                return false;
              }}
              showUploadList={false}
              multiple={false}
            >
              <div className="sparse-new-card sparse-new-card--upload">
                <div className="sparse-new-card-icon sparse-new-card-icon--upload">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                </div>
                <span className="sparse-new-card-label">{isEmbroideryTrace ? "Image to Embroidery" : "Image to SVG"}</span>
                <span className="sparse-new-card-sub">{isEmbroideryTrace ? "JPG · PNG · SVG → Embroidery" : "JPG · PNG · WebP → SVG"}</span>
              </div>
            </Upload>

            {/* Temporarily hidden: Generate with AI entry point.
            <div
              className="sparse-new-card sparse-new-card--ai"
              onClick={() => {
                if (isNativeiOS() && !userIsPremium) { setShowIOSPaywall(true); return; }
                if (setIsImageGeneratorModalVisible) setIsImageGeneratorModalVisible(true);
              }}
            >
              <div className="sparse-new-card-icon sparse-new-card-icon--ai">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </div>
              <span className="sparse-new-card-label">Generate with AI</span>
              <span className="sparse-new-card-sub">{isEmbroideryTrace ? "Text prompt → Embroidery" : "Text prompt → SVG"}</span>
            </div>
            */}

            {isEmbroideryTrace && (
              <div
                className="sparse-new-card sparse-new-card--examples"
                onClick={() => startTutorial('embroidery', PICK_DESIGN_STEP)}
              >
                {/* Until something's finished this card has to earn the click,
                    so it names the payoff and what it costs rather than the
                    activity — "tutorials" sounds like an evening, and the first
                    one is a few minutes. */}
                {completedExamples === 0 && (
                  <span className="sparse-new-card-badge sparse-new-card-badge--start">
                    Start here
                  </span>
                )}
                {nextLesson && completedExamples > 0 && (
                  <span className="sparse-new-card-badge">
                    {lessonsLeft} to go
                  </span>
                )}
                <div className="sparse-new-card-icon sparse-new-card-icon--examples">
                  <IonIcon icon={schoolOutline} />
                </div>
                <span className="sparse-new-card-label">Academy</span>
                <span className="sparse-new-card-sub">
                  {!nextLesson
                    ? `All ${TUTORIAL_DESIGNS.length} finished — replay any time`
                    : completedExamples > 0
                      ? `Next: ${nextLesson.name} · ${nextLesson.minutes} min →`
                      : `Your first stitch file in ${nextLesson.minutes} min →`}
                </span>
                {completedExamples > 0 && (
                  <span className="academy-progress" aria-hidden="true">
                    {TUTORIAL_DESIGNS.map((design) => (
                      <span
                        key={design.id}
                        className={`academy-progress-pip${
                          completedLessons?.has(design.tutorialId) ? ' is-done' : ''
                        }`}
                      />
                    ))}
                  </span>
                )}
              </div>
            )}

          </div>

          <div className="sparse-section-header sparse-section-header--recent">
            <h3 className="sparse-section-label">Recent</h3>
            {loadingRecent ? (
              <span className="recent-loading-label" role="status">Loading recent designs…</span>
            ) : (
              <span className="sparse-file-count">{recentDesigns.length}</span>
            )}
          </div>

          {loadingRecent ? (
            <div className="inner-container" aria-busy="true" aria-label="Recent designs">
              {Array.from({ length: 12 }, (_, index) => (
                <div key={index} className="recent-design-placeholder" aria-hidden="true" />
              ))}
            </div>
          ) : isSparse ? (
            <div className="sparse-recent-row">
              {recentDesigns.map((file, index) => (
                <div
                  key={`${file.id}-${index}`}
                  className="sparse-recent-card"
                  ref={index === recentDesigns.length - 1 ? lastElementRef : null}
                >
                  <Card file={file} index={index} />
                </div>
              ))}
              {[...Array(phantomCount)].map((_, i) => (
                <div key={`phantom-${i}`} className="sparse-phantom-card" aria-hidden="true" />
              ))}
            </div>
          ) : (
            <div className="inner-container">
              {recentDesigns.map((file, index) => (
                <div key={`${file.id}-${index}-${viewportWidth > 768 ? 'desktop' : 'mobile'}`}>
                  <Card file={file} index={index} />
                </div>
              ))}

              {hasMore && (
                <div
                  ref={lastElementRef}
                  style={{ width: "100%", padding: "20px", textAlign: "center", gridColumn: "1 / -1" }}
                >
                  {isLoadingMore && "Loading more..."}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showIOSPaywall && (
        <IOSPaywall
          standalone
          action="generate"
          onClose={() => setShowIOSPaywall(false)}
          onSuccess={() => setShowIOSPaywall(false)}
        />
      )}
    </>
  );
};

export default All;
